import { Text } from '@shared/components';
import { ActionTimelineEvent } from '@shared/components/events-timeline/components/action-timeline-event';
import { InlineTextField } from '@shared/components/inline-text-field/InlineTextField';
import { TextArea } from '@shared/components/react-hook-form';
import { useToast } from '@shared/components/toast';
import {
  TriggerPortalAppointmentSchedulingMutation,
  useTriggerPortalAppointmentSchedulingMutation as useSchedulePortal,
} from '@shared/generated/graphql';
import { parseTime } from 'clerk_common/features/parseTime';
import {
  isDatetimePickedByPortalAutomation,
  toPortalLabel,
} from 'clerk_common/features/scheduleUtil';
import { PortalDetails, ScheduleStop } from 'clerk_common/types';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { humanizeDate } from '../../util';
import { ApptStatusChip } from './ApptStatusChip';
import { ScheduleButton, StopProps } from './Stop';
import {
  createDateTime,
  formatParsedTime,
  formatTime,
  knownAndInProgress,
  notYetScheduledOrUnknown,
} from './util';

type StopForm = {
  time: string;
  notes: string;
};

export function WebPortalStop({
  stop,
  index,
  shipmentId,
  refetchShipment,
}: StopProps) {
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(true);
  const { sendToast } = useToast();
  const defaultValues: StopForm = getDefaultValues(stop);
  const methods = useForm<StopForm>({ defaultValues });
  const { control, getValues } = methods;

  const options = { onCompleted: onPortalCompleted };
  const [triggerPortal] = useSchedulePortal(options);

  const status = stop.appointmentStatus;
  const isNotYetScheduled = notYetScheduledOrUnknown(status);
  const isInProgress = knownAndInProgress(status);
  const type = stop.placeSchedulePortalType;
  const hideDateAndTime = isDatetimePickedByPortalAutomation(type);

  async function scheduleStop() {
    const time = parseTime(methods.getValues('time'));
    if (time === undefined && !hideDateAndTime) {
      sendToast('Please enter a valid time.');
      return;
    }
    if (!stop.day) {
      sendToast('Could not request unknown date.');
      return;
    }
    let dt;
    if (time && !hideDateAndTime) {
      dt = createDateTime(stop.day, time, stop.placeTimeZone);
    }
    const stopsIndexToSchedule = index;
    const details: PortalDetails = {
      scheduleNotes: getValues('notes'),
    };
    const input = {
      shipmentId,
      stopsIndexToSchedule,
      dt,
      movementStopId: stop.movementStopId,
      details,
    };
    setSubmitEnabled(false);
    await triggerPortal({ variables: { input } });
  }

  async function onPortalCompleted(
    data: TriggerPortalAppointmentSchedulingMutation
  ) {
    const { success, error } = data.triggerPortalAppointmentScheduling;
    if (!success && error) {
      sendToast(error);
    }
    if (success) {
      await refetchShipment();
    }
    setSubmitEnabled(true);
  }

  function formatTime() {
    if (methods.getValues('time').length == 0) return;
    const unparsedTime = methods.getValues('time');
    const parsedTime = parseTime(unparsedTime);
    if (parsedTime) {
      methods.setValue('time', formatParsedTime(parsedTime));
    } else {
      methods.setValue('time', '');
    }
  }

  // TODO(jonathan): disable schedule button if form is not valid
  return (
    <FormProvider {...methods}>
      <ActionTimelineEvent type={stop.stopType}>
        <div className="mb-4 ml-2 mt-1 text-gray-700">
          <Text isHeavy type="body-xs">
            {stop.locationLabel}
          </Text>
          <Text type="body-xs">{stop.address.addressOne}</Text>
          {!hideDateAndTime && (
            <Text type="body-xs">{humanizeDate(stop.day)}</Text>
          )}
          {isNotYetScheduled && (
            <>
              <div className="my-3 rounded-md border bg-gray-50 p-2">
                <Text type="body-sm">{toPortalLabel(type)}</Text>
                {!hideDateAndTime && (
                  <InlineTimeField formatTime={formatTime} />
                )}
                <Text type="body-sm" className="mt-2 text-gray-500">
                  Notes
                </Text>
                <TextArea type="body-sm" control={control} name="notes" />
              </div>
              <div>
                <ScheduleButton
                  label="Request Appointment"
                  onPress={scheduleStop}
                  disabled={!submitEnabled}
                />
              </div>
            </>
          )}
          {isInProgress && <ApptStatusChip stop={stop} />}
        </div>
      </ActionTimelineEvent>
    </FormProvider>
  );
}

function InlineTimeField({ formatTime }: { formatTime: () => void }) {
  return (
    <InlineTextField
      name="time"
      label="Time"
      type="text"
      save={formatTime}
      required={false}
      className="mb-1"
    />
  );
}

function getDefaultValues(stop: ScheduleStop): StopForm {
  const values: StopForm = {
    notes: stop.scheduleNotes ?? '',
    time: stop.suggestedDt
      ? formatTime(stop.suggestedDt, stop.placeTimeZone)
      : '',
  };
  return values;
}
